import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { useIntl } from "react-intl";
import { useLiveQuery } from "dexie-react-hooks";
import { AiOutlineLeft } from "react-icons/ai";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";

import { db } from "../../../db/db";
import {
  loadCompaniesDistribuidorPratica,
  loadEquipmentById,
  loadLocationById,
  loadStoreById,
} from "../../../services";

import { useScopedDashboard } from "../../../hooks/useScopedDashboard";
import {
  RecipeExecRow,
  useRecipeDashboardStats,
} from "../../../hooks/useRecipeDashboardStats";
import { usePeakUsage } from "../../../hooks/usePeakUsage";
import { useEnergyConsumption } from "../../../hooks/useEnergyConsumption";
import { createScrollHandler } from "../../../utils/createScrollHandler";
import {
  loadGroupLocationDashboardContext,
  saveGroupLocationDashboardContext,
} from "../../../utils/groupLocationDashboardContext";

import PrimaryButton from "../../../components/FormElements/PrimaryButton";
import InputChecked from "../../../components/FormElements/InputChecked";
import GenericTable from "../../../components/UIelements/GenericTable";
import { DoughnutAnalysis } from "../../../components/UIelements/GraphicDoughnutEquipment";
import { GenericGraphicBar } from "../../../components/UIelements/GenericGraphicBar";

import ModalUpdateStore from "../../../components/Modals/ModalEditStore";
import ModalUpdateLocation from "../../../components/Modals/ModalEditLocation";
import ModalUnlinkEquipment from "../../../components/Modals/ModalUnlinkEquipment";
import ModalEditEquipment from "../../../components/Modals/ModalEditEquipment";

import {
  HeaderSkeleton,
  EquipmentTableSkeleton,
  StatsSkeleton,
} from "../../../components/Skeletons/GroupLocationDashboard";
import EnergyConsumptionSkeleton from "../../../components/Skeletons/EnergyConsumptionSkeleton";

import type { Store } from "../../../models/Stores";
import type { Location as LocationModel } from "../../../models/Location";
import type { ScopedEquipmentList } from "../../../models/DashboardDetails";
import type { DashboardOrigin } from "../../../utils/groupLocationDashboardContext";
import type { ColumnDefinitionType } from "../../../components/UIelements/GenericTable/genericTableTypes";
import NoContent from "../../../components/UIelements/NoContent";

import * as Styled from "./styles";
import { ListType } from "@praticabr/ppsa";
import { useHeader } from "../../../context/HeaderContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend,
);

const columns: ColumnDefinitionType<
  ScopedEquipmentList.Item,
  keyof ScopedEquipmentList.Item
>[] = [
  {
    key: "statusOnOff",
    gridArea: "title",
    header: "user.Status",
    isSortable: false,
    width: "3%",
  },
  {
    key: "name",
    gridArea: "data1",
    header: "equipment.nome",
    isSortable: true,
    width: "25%",
  },
  {
    key: "typeEquipment",
    gridArea: "data3",
    header: "equipment.modelo",
    isSortable: true,
    width: "25%",
  },
  {
    key: "userName",
    gridArea: "data2",
    header: "user.Usuário",
    isSortable: true,
    width: "25%",
  },
  {
    key: "serialNumber",
    gridArea: "data4",
    header: "equipment.Número de Série",
    isSortable: false,
    width: "25%",
  },
  {
    key: "menuName",
    gridArea: "data5",
    header: "equipment.Menu",
    isSortable: false,
    width: "25%",
  },
];

type DashboardRouteState = {
  entityType?: DashboardOrigin;
  entityId?: number;
};

type HeaderInfo = {
  name: string;
  city: string;
  address: string;
  postalCode: string;
};

const emptyHeaderInfo: HeaderInfo = {
  name: "",
  city: "",
  address: "",
  postalCode: "",
};

const formatCityState = (location?: LocationModel | null) => {
  if (!location) return "";
  const city = location.city?.trim();
  const state = location.state?.trim();
  if (city && state) return `${city} - ${state}`;
  return city || state || "";
};

const formatAddressLine = (location?: LocationModel | null) => {
  if (!location) return "";
  const street = location.street?.trim();
  const number = location.number ? String(location.number) : "";
  const neighborhood = location.neighborhood?.trim();

  const parts: string[] = [];
  if (street) {
    parts.push(number ? `${street}, ${number}` : street);
  } else if (number) {
    parts.push(number);
  }

  if (neighborhood) {
    parts.push(`- ${neighborhood}`);
  }

  return parts.join(" ").trim();
};

const formatPostalCode = (zipCode?: string | number | null) => {
  if (zipCode === null || zipCode === undefined) return "";
  const digits = String(zipCode).replace(/\D/g, "");
  if (digits.length === 8) {
    return `${digits.substring(0, 5)}-${digits.substring(5)}`;
  }
  return digits;
};

const GroupLocationDashboard: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const storedContext = useMemo(() => loadGroupLocationDashboardContext(), []);
  const routeState = (routeLocation.state ?? {}) as DashboardRouteState;
  const resolvedEntityType: DashboardOrigin =
    routeState.entityType ?? storedContext?.entityType ?? "location";
  const resolvedEntityId = routeState.entityId ?? storedContext?.entityId;
  const normalizedEntityId =
    typeof resolvedEntityId === "number" ? resolvedEntityId : undefined;
  const isGroupView = resolvedEntityType === "group";
  const userDb = useLiveQuery(() => db.user.toArray());
  const companyDb = useLiveQuery(() => db.company.toArray());
  const currentUser = userDb?.[0];
  const currentCompany = companyDb?.[0];
  const [storeDetails, setStoreDetails] = useState<Store | null>(null);
  const [locationDetails, setLocationDetails] = useState<LocationModel | null>(
    null,
  );
  const [headerInfo, setHeaderInfo] = useState<HeaderInfo>({
    ...emptyHeaderInfo,
  });
  const [isHeaderLoading, setIsHeaderLoading] = useState<boolean>(false);
  const [showModalEditGroup, setShowModalEditGroup] = useState(false);
  const [showModalEditLocation, setShowModalEditLocation] = useState(false);
  const [showModalEditEquipment, setShowModalEditEquipment] = useState(false);
  const [showModalUnlinkEquipment, setShowModalUnlinkEquipment] =
    useState<boolean>(false);
  const [companyOptions, setCompanyOptions] = useState<
    ListType<string | number>[]
  >([]);
  const [expand, setExpand] = useState<boolean>(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);

  const tableColumns: ColumnDefinitionType<
    RecipeExecRow,
    keyof RecipeExecRow
  >[] = [
    {
      key: "equipment",
      header: "cookbook.Equipamento",
      isSortable: false,
      gridArea: "title",
      width: "40%",
    },
    {
      key: "recipe",
      header: "equipment.Receita",
      isSortable: false,
      gridArea: "data1",
      width: "40%",
    },
    {
      key: "count",
      header: "equipment.Quantidade",
      isSortable: false,
      gridArea: "data2",
      width: "10%",
    },
  ];

  const scopeParams = {
    scopeType: resolvedEntityType,
    scopeId: normalizedEntityId,
  };

  const {
    equipmentList,
    loading: isEquipmentLoading,
    hasMore: hasMoreEquipment,
    loadMore: loadMoreEquipment,
    refresh: refreshEquipmentList,
  } = useScopedDashboard({
    ...scopeParams,
    currentUser,
  });

  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    recipesData,
    usageSummary,
    loading: isStatsLoading,
    hasMore: hasMoreStats,
    loadMore: loadMoreStats,
  } = useRecipeDashboardStats({
    ...scopeParams,
  });

  const { chartData: peakSummary, loading: isPeakLoading } = usePeakUsage({
    ...scopeParams,
  });

  const [orderBy, setOrderBy] = useState<"id" | "equipment" | "recipe">("id");

  const sortedRecipeExecutions = useMemo(() => {
    return [...recipesData].sort((a, b) => {
      if (orderBy === "equipment")
        return a.equipment.localeCompare(b.equipment);
      if (orderBy === "recipe") return a.recipe.localeCompare(b.recipe);
      return a.id - b.id;
    });
  }, [recipesData, orderBy]);

  const {
    summary: energySummary,
    loading: isEnergyLoading,
    hasToro: showEnergySection,
    hasLogs: showEnergyLogs,
    period: energyPeriod,
    handlePeriodChange,
    chartConfig: energyBarData,
  } = useEnergyConsumption({ ...scopeParams });

  useEffect(() => {
    if (normalizedEntityId) {
      saveGroupLocationDashboardContext({
        entityType: resolvedEntityType,
        entityId: normalizedEntityId,
      });
    }
  }, [normalizedEntityId, resolvedEntityType]);

  const fetchHeaderDetails = useCallback(async () => {
    if (!currentUser || !normalizedEntityId) {
      setIsHeaderLoading(false);
      setStoreDetails(null);
      setLocationDetails(null);
      setHeaderInfo({ ...emptyHeaderInfo });
      return;
    }

    setIsHeaderLoading(true);
    setHeaderInfo({ ...emptyHeaderInfo });

    if (isGroupView) {
      setLocationDetails(null);
    } else {
      setStoreDetails(null);
    }

    try {
      if (isGroupView) {
        const response = await loadStoreById(
          normalizedEntityId,
          currentUser.accessToken,
        );
        const storeData: Store | undefined =
          response.data?.store ?? response.data;
        setStoreDetails(storeData ?? null);
        setHeaderInfo({
          name: storeData?.storeName ?? "",
          city: "",
          address: "",
          postalCode: "",
        });
      } else {
        const response = await loadLocationById(
          normalizedEntityId,
          currentUser.accessToken,
        );
        const locationData: LocationModel | undefined =
          response.data?.location ?? response.data;
        setLocationDetails(locationData ?? null);
        setHeaderInfo({
          name: locationData?.locationName ?? "",
          city: formatCityState(locationData),
          address: formatAddressLine(locationData),
          postalCode: formatPostalCode(locationData?.zipCode),
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsHeaderLoading(false);
    }
  }, [currentUser, normalizedEntityId, isGroupView]);

  useEffect(() => {
    fetchHeaderDetails();
  }, [fetchHeaderDetails]);

  const energyBarOptions = useMemo<ChartOptions<"bar">>(() => {
    const barThickness = energyPeriod === "week" ? 38 : 80;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            maxRotation: 0,
            callback: function (value, index, ticks) {
              const label = this.getLabelForValue(value as number);
              return label ? label : "";
            },
          },
          grid: { display: false },
          title: {
            display: true,
            text:
              energyPeriod === "week"
                ? intl.formatMessage({ id: "equipment.Semana" })
                : intl.formatMessage({ id: "equipment.Mês" }),
            align: "start",
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: "kWh" },
        },
      },
      datasets: {
        bar: {
          barThickness,
          maxBarThickness: barThickness,
          categoryPercentage: 1,
          barPercentage: 1,
        },
      },
    };
  }, [energyPeriod, intl]);

  const openModalEdit = useCallback(
    (page: DashboardOrigin) => {
      if (page === "group") {
        if (!storeDetails) return;
        setShowModalEditGroup(true);
      } else if (page === "location") {
        if (!locationDetails) return;
        setShowModalEditLocation(true);
      }
    },
    [locationDetails, storeDetails],
  );

  const openModalUnlinkEquipment = useCallback((id: number) => {
    setSelectedId(id);
    setShowModalUnlinkEquipment(true);
  }, []);

  const openModalEditEquipment = useCallback(
    async (id: number) => {
      if (!currentUser) return;

      try {
        const equipmentResponse = await loadEquipmentById(
          id,
          currentUser.accessToken,
        );
        setSelectedId(id);
        setSelectedEquipment(equipmentResponse.data);
        setShowModalEditEquipment(true);
      } catch (error) {
        console.error(error);
        return;
      }

      try {
        const companyResponse = await loadCompaniesDistribuidorPratica(
          currentUser.accessToken,
        );
        const options = companyResponse.data.map(
          (company: any): ListType<string | number> => ({
            value: company.id,
            label: company.corporateName,
          }),
        );

        options.unshift({
          value: 0,
          label: intl.formatMessage({ id: "equipment.Encerrar acesso remoto" }),
        });

        setCompanyOptions(options);
      } catch (error) {
        console.error(error);
      }
    },
    [currentUser, intl],
  );

  const submitModalUnlinkEquipment = useCallback(async () => {
    refreshEquipmentList();
  }, [refreshEquipmentList]);

  const submitModalEditEquipment = useCallback(async () => {
    refreshEquipmentList();
  }, [refreshEquipmentList]);

  const handleStoreUpdated = useCallback(() => {
    setShowModalEditGroup(false);
    fetchHeaderDetails();
  }, [fetchHeaderDetails]);

  const handleLocationUpdated = useCallback(() => {
    setShowModalEditLocation(false);
    fetchHeaderDetails();
  }, [fetchHeaderDetails]);

  const handleScrollEquipmentTable = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      createScrollHandler(
        isEquipmentLoading,
        hasMoreEquipment,
        loadMoreEquipment,
        scrollTimeoutRef,
      )(e);
    },
    [isEquipmentLoading, hasMoreEquipment, loadMoreEquipment],
  );

  const handleScrollRecipeStatsTable = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      createScrollHandler(
        isStatsLoading,
        hasMoreStats,
        loadMoreStats,
        scrollTimeoutRef,
      )(e);
    },
    [isStatsLoading, hasMoreStats, loadMoreStats],
  );

  const handleEquipment = async (id: number) => {
    if (!equipmentList) return;

    const equipment = equipmentList.find((e) => e.id === id);
    if (!equipment) return;

    navigate("/equipment/" + equipment.serialNumber, {
      state: { equipmentData: equipment },
    });
  };

  const fallbackValue = isHeaderLoading ? "..." : "--";
  const headerName = headerInfo.name?.trim() ? headerInfo.name : fallbackValue;
  const headerCity = headerInfo.city?.trim() ? headerInfo.city : fallbackValue;
  const headerAddress = headerInfo.address?.trim()
    ? headerInfo.address
    : fallbackValue;
  const headerPostalCode = headerInfo.postalCode?.trim()
    ? headerInfo.postalCode
    : fallbackValue;

  const canEditEntity =
    Boolean(normalizedEntityId) &&
    (isGroupView ? Boolean(storeDetails) : Boolean(locationDetails));

  const headerConfig = useMemo(() => {
    return {
      showBack: true,
      backLabel: intl.formatMessage({ id: "menus.VOLTAR" }),
      backAction: () => (expand ? setExpand(false) : navigate("/equipment")),
    };
  }, [expand, navigate, intl]);

  useHeader(headerConfig);

  const renderEditButton = () => (
    <PrimaryButton
      model="confirmation-gradient"
      type="button"
      size="large"
      width="100px"
      disabled={!canEditEntity || isHeaderLoading}
      onClick={() => openModalEdit(isGroupView ? "group" : "location")}
    >
      {intl.formatMessage({ id: "dashboard.editar" })}
    </PrimaryButton>
  );

  return (
    <>
      <Styled.PageWrapper>
        <>
          {isHeaderLoading ? (
            <HeaderSkeleton />
          ) : (
            <Styled.HeaderCard>
              <Styled.HeaderTop>
                <Styled.LocationInfo>
                  <Styled.LocationNameRow>
                    <Styled.LocationName>{headerName}</Styled.LocationName>
                    <Styled.MobileHeaderActions className="mobile-action">
                      {renderEditButton()}
                    </Styled.MobileHeaderActions>
                  </Styled.LocationNameRow>
                  {!isGroupView && (
                    <>
                      <Styled.LocationCity>{headerCity}</Styled.LocationCity>
                      <Styled.LocationAddress>
                        <span>
                          {intl.formatMessage({ id: "dashboard.endereço:" })}
                        </span>
                        <strong>{headerAddress}</strong>
                        <span className="cep">
                          {intl.formatMessage({ id: "dashboard.cep:" })}
                        </span>
                        <strong>{headerPostalCode}</strong>
                      </Styled.LocationAddress>
                    </>
                  )}
                </Styled.LocationInfo>
                <Styled.HeaderActions>
                  {renderEditButton()}
                </Styled.HeaderActions>
              </Styled.HeaderTop>
            </Styled.HeaderCard>
          )}

          {isEquipmentLoading && equipmentList.length === 0 ? (
            <EquipmentTableSkeleton />
          ) : (
            <Styled.SectionCard>
              <Styled.SectionTitleRow className="equipment">
                <Styled.SectionTitle>
                  {intl.formatMessage({ id: "menus.Equipamentos associados" })}
                </Styled.SectionTitle>
                {equipmentList.length ? (
                  <GenericTable
                    className="equipment-table force-scroll pointer"
                    data={equipmentList}
                    columns={columns}
                    showMobileHeaders
                    moreButton={{
                      show: true,
                      getTitle: (equipment) => equipment.name,
                      options: [
                        {
                          value: "equipment.Editar",
                          action: openModalEditEquipment,
                        },
                        {
                          value: "equipment.Desvincular",
                          action: openModalUnlinkEquipment,
                          alert: true,
                        },
                      ],
                    }}
                    onOrderTable={(column) => setOrderBy(column as any)}
                    onScroll={handleScrollEquipmentTable}
                    onRowClicked={handleEquipment}
                  />
                ) : (
                  <NoContent
                    multilist
                    title={intl.formatMessage({ id: "equipment.sem conteúdo" })}
                  />
                )}
              </Styled.SectionTitleRow>
            </Styled.SectionCard>
          )}
        </>

        {isStatsLoading ? (
          <StatsSkeleton />
        ) : (
          <Styled.SectionCard>
            <Styled.SectionTitleRow>
              <Styled.SectionTitle>
                {intl.formatMessage({ id: "dashboard.estatísticas" })}
              </Styled.SectionTitle>
              <Styled.StatsContainer>
                <Styled.StatsGrid>
                  <Styled.UsagePanel>
                    <Styled.PanelTitle>
                      {intl.formatMessage({
                        id: "dashboard.uso de equipamento",
                      })}
                    </Styled.PanelTitle>
                    {sortedRecipeExecutions.length ? (
                      <Styled.DoughnutWrapper>
                        <DoughnutAnalysis dataProps={usageSummary} />
                      </Styled.DoughnutWrapper>
                    ) : (
                      <NoContent
                        multilist
                        title={intl.formatMessage({
                          id: "equipment.AINDA NÃO HÁ DADOS DE RECEITAS AQUI :(",
                        })}
                      />
                    )}
                  </Styled.UsagePanel>

                  <Styled.VerticalDivider aria-hidden />

                  <Styled.RecipePanel>
                    <Styled.PanelTitle>
                      {intl.formatMessage({
                        id: "dashboard.receitas mais executadas por equipamento",
                      })}
                    </Styled.PanelTitle>

                    {sortedRecipeExecutions.length ? (
                      <GenericTable
                        id=""
                        className="full-width-mobile cursorPointer"
                        data={sortedRecipeExecutions}
                        columns={tableColumns}
                        onOrderTable={(column) => setOrderBy(column as any)}
                        onScroll={handleScrollRecipeStatsTable}
                      />
                    ) : (
                      <NoContent
                        multilist
                        title={intl.formatMessage({
                          id: "equipment.AINDA NÃO HÁ DADOS DE RECEITAS AQUI :(",
                        })}
                      />
                    )}
                  </Styled.RecipePanel>
                </Styled.StatsGrid>

                <Styled.PeakSection>
                  <Styled.PanelTitle>
                    {intl.formatMessage({ id: "dashboard.pico de uso" })}
                  </Styled.PanelTitle>
                  {peakSummary ? (
                    <Styled.ChartWrapper>
                      <GenericGraphicBar
                        dataProps={peakSummary}
                        showLegend
                        valueUnit="h"
                      />
                    </Styled.ChartWrapper>
                  ) : (
                    <NoContent
                      multilist
                      isVertical
                      title={intl.formatMessage({
                        id: "equipment.AINDA NÃO HÁ DADOS DE RECEITAS AQUI :(",
                      })}
                    />
                  )}
                </Styled.PeakSection>
              </Styled.StatsContainer>
            </Styled.SectionTitleRow>
          </Styled.SectionCard>
        )}

        {(isEnergyLoading || showEnergySection) && (
          <>
            {isEnergyLoading ? (
              <EnergyConsumptionSkeleton TitleBox={""} />
            ) : (
              <Styled.SectionCard>
                <Styled.SectionTitleRow>
                  <Styled.SectionTitle>
                    {intl.formatMessage({
                      id: "dashboard.Consumo de energia - Lastro Toro TC",
                    })}
                  </Styled.SectionTitle>

                  {showEnergyLogs ? (
                    <Styled.EnergyWrapper>
                      <Styled.EnergyCards>
                        <Styled.EnergyCard>
                          <Styled.EnergyCardLabel>
                            {intl.formatMessage({
                              id: "dashboard.TOROS TC CONECTADOS:",
                            })}
                          </Styled.EnergyCardLabel>
                          <Styled.EnergyCardValue>
                            {energySummary.connected}
                          </Styled.EnergyCardValue>
                        </Styled.EnergyCard>
                        <Styled.EnergyCard>
                          <Styled.EnergyCardLabel>
                            {intl.formatMessage({
                              id: "equipment.CONSUMO ESTIMADO",
                            }) + ":"}
                          </Styled.EnergyCardLabel>
                          <Styled.EnergyCardValueRow>
                            <Styled.EnergyCardValue>
                              {energySummary.estimated}
                            </Styled.EnergyCardValue>
                            <Styled.EnergyCardUnit>kWh</Styled.EnergyCardUnit>
                          </Styled.EnergyCardValueRow>
                        </Styled.EnergyCard>
                        <Styled.EnergyCard className="borderLeftBlue">
                          <Styled.EnergyCardLabel>
                            {intl.formatMessage({
                              id: "equipment.CONSUMO ACUMULADO",
                            }) + ":"}
                          </Styled.EnergyCardLabel>
                          <Styled.EnergyCardValueRow>
                            <Styled.EnergyCardValue>
                              {energySummary.accumulated}
                            </Styled.EnergyCardValue>
                            <Styled.EnergyCardUnit>kWh</Styled.EnergyCardUnit>
                          </Styled.EnergyCardValueRow>
                        </Styled.EnergyCard>
                        <Styled.EnergyCard className="borderLeftGreen">
                          <Styled.EnergyCardLabel>
                            {intl.formatMessage({
                              id: "equipment.ECONOMIA COM MODO ECO",
                            }) + ":"}
                          </Styled.EnergyCardLabel>
                          <Styled.EnergyCardValueRow>
                            <Styled.EnergyCardValue>
                              {energySummary.savings}
                            </Styled.EnergyCardValue>
                            <Styled.EnergyCardUnit>kWh</Styled.EnergyCardUnit>
                          </Styled.EnergyCardValueRow>
                        </Styled.EnergyCard>
                        <Styled.EnergyCompactStack>
                          <Styled.EnergyCompactCard>
                            <Styled.EnergyCompactValueRow>
                              <Styled.EnergyCardLabel>
                                {intl.formatMessage({
                                  id: "equipment.MÉDIA SEMANAL",
                                }) + ":"}
                              </Styled.EnergyCardLabel>
                              <Styled.EnergyCompactValue>
                                {energySummary.avgWeekly}
                              </Styled.EnergyCompactValue>
                              <Styled.EnergyCompactUnit>
                                kWh
                              </Styled.EnergyCompactUnit>
                            </Styled.EnergyCompactValueRow>
                          </Styled.EnergyCompactCard>
                          <Styled.EnergyCompactCard>
                            <Styled.EnergyCompactValueRow>
                              <Styled.EnergyCardLabel>
                                {intl.formatMessage({
                                  id: "equipment.MÉDIA MENSAL",
                                }) + ":"}
                              </Styled.EnergyCardLabel>
                              <Styled.EnergyCompactValue>
                                {energySummary.avgMonthly}
                              </Styled.EnergyCompactValue>
                              <Styled.EnergyCompactUnit>
                                kWh
                              </Styled.EnergyCompactUnit>
                            </Styled.EnergyCompactValueRow>
                          </Styled.EnergyCompactCard>
                        </Styled.EnergyCompactStack>
                      </Styled.EnergyCards>

                      <Styled.EnergyContent>
                        <Styled.EnergyChart>
                          <Bar
                            data={energyBarData}
                            options={energyBarOptions}
                          />
                        </Styled.EnergyChart>

                        <Styled.EnergyToggle>
                          <InputChecked
                            id="energy-options"
                            type="radio"
                            label={"equipment.Semana"}
                            name={"energy-options"}
                            value={"week"}
                            default={energyPeriod === "week"}
                            initialValue={energyPeriod}
                            initialValid
                            onInput={handlePeriodChange}
                            labelName={"week"}
                          />
                          <InputChecked
                            id="energy-options"
                            type="radio"
                            label={"equipment.Mês"}
                            name={"energy-options"}
                            value={"month"}
                            default={energyPeriod === "month"}
                            initialValue={energyPeriod}
                            initialValid
                            onInput={handlePeriodChange}
                            labelName={"month"}
                          />
                        </Styled.EnergyToggle>
                      </Styled.EnergyContent>
                    </Styled.EnergyWrapper>
                  ) : (
                    <NoContent
                      multilist
                      title={intl.formatMessage({
                        id: "equipment.AINDA NÃO HÁ DADOS DE CONSUMO DE ENERGIA AQUI :(",
                      })}
                    />
                  )}
                </Styled.SectionTitleRow>
              </Styled.SectionCard>
            )}
          </>
        )}
      </Styled.PageWrapper>

      {showModalEditGroup && storeDetails && (
        <ModalUpdateStore
          store={storeDetails}
          onCancel={() => setShowModalEditGroup(false)}
          onUpdateList={handleStoreUpdated}
        />
      )}

      {showModalEditLocation && locationDetails && (
        <ModalUpdateLocation
          location={locationDetails}
          onCancel={() => setShowModalEditLocation(false)}
          onUpdateList={handleLocationUpdated}
        />
      )}

      {showModalUnlinkEquipment && selectedId !== null && currentUser && (
        <ModalUnlinkEquipment
          id={selectedId}
          token={currentUser.accessToken}
          onCancel={() => setShowModalUnlinkEquipment(false)}
          onUpdateList={submitModalUnlinkEquipment}
        />
      )}

      {showModalEditEquipment && selectedEquipment && currentUser && (
        <ModalEditEquipment
          companyId={currentCompany?.id ?? 0}
          equipment={selectedEquipment}
          onCancel={() => setShowModalEditEquipment(false)}
          onUpdateList={submitModalEditEquipment}
          token={currentUser.accessToken}
          user={currentUser}
          companyOptions={companyOptions}
        />
      )}
    </>
  );
};

export default GroupLocationDashboard;
